import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Eye,
  Phone,
  Wallet,
  RefreshCw,
  AlertTriangle,
  Layers,
  Clock,
  ShieldAlert,
} from "lucide-react";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { fintechApi } from "../services/api";

const { RangePicker } = DatePicker;

// Account status styling — LoanAccountDetail wale hi tokens
const STATUS_CONFIG = {
  ACTIVE: { label: "Active", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  OVERDUE: { label: "Overdue", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  NPA: { label: "NPA", bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
  CLOSED: { label: "Closed", bg: "bg-slate-100", text: "text-slate-800", border: "border-slate-300" },
};

// Quick views — status param ki jagah dedicated endpoints, isliye source alag rakha hai
const VIEWS = [
  { key: "ALL", label: "All Accounts", icon: Layers },
  { key: "OVERDUE", label: "Overdue", icon: Clock },
  { key: "NPA", label: "NPA", icon: ShieldAlert },
];

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

// Backend kabhi array, kabhi page object bhejta hai — sab handle
const toArray = (res) =>
  Array.isArray(res)
    ? res
    : Array.isArray(res?.content)
    ? res.content
    : Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res?.data?.content)
    ? res.data.content
    : Array.isArray(res?.accounts)
    ? res.accounts
    : [];

// Field naming backend me mix hai — detail page jaisa hi normalize
const normalizeAccount = (a = {}, idx = 0) => ({
  ...a,
  id: a.id ?? a.loanAccountId ?? a.accountId ?? idx,
  accountNumber: a.accountNumber || a.loanAccountNumber || (a.id ? `#${a.id}` : "-"),
  borrowerName: a.borrowerName || a.applicantName || a.customerName || "-",
  phone: a.phone || a.contactNumber || a.mobile || "",
  principalAmount: Number(a.principalAmount ?? a.sanctionedAmount ?? a.loanAmount) || 0,
  monthlyEmi: Number(a.monthlyEmi ?? a.emiAmount) || 0,
  totalOutstanding: Number(a.totalOutstanding ?? a.outstandingAmount) || 0,
  totalOverdue: Number(a.totalOverdue ?? a.overdueAmount) || 0,
  dpd: Number(a.dpd) || 0,
  status: a.status || a.accountStatus || "ACTIVE",
});

export default function LoanAccountList() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const { has } = usePermissions();
  const primaryColor = currentTheme?.color || "#2e856e";
  const canView = has("LOAN_ACCOUNT_VIEW");
  const [searchParams] = useSearchParams();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Dashboard cards `?status=` / `?view=` bhej sakte hain
  const [view, setView] = useState(() => {
    const v = (searchParams.get("view") || "").toUpperCase();
    return VIEWS.some((x) => x.key === v) ? v : "ALL";
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "ALL");
  const [dateRange, setDateRange] = useState(null);

  const isQuickView = view !== "ALL";

  // Har keystroke pe API call na ho
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      let res;
      if (view === "OVERDUE") {
        res = await fintechApi.getOverdueLoans();
      } else if (view === "NPA") {
        res = await fintechApi.getNpaLoans();
      } else {
        const params = {};
        if (statusFilter !== "ALL") params.status = statusFilter;
        if (debouncedSearch) params.search = debouncedSearch;
        if (dateRange && dateRange[0] && dateRange[1]) {
          params.startDate = dateRange[0].format("YYYY-MM-DD");
          params.endDate = dateRange[1].format("YYYY-MM-DD");
        }
        res = await fintechApi.getLoanAccounts(params);
      }
      setAccounts(toArray(res).map(normalizeAccount));
    } catch (err) {
      console.error("Loan accounts load failed:", err);
      // Mock list kabhi nahi — error dikhega, Retry button milega
      setLoadError(err.message || "Failed to load loan accounts");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [view, statusFilter, debouncedSearch, dateRange]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Safety net — backend search param ignore kare to bhi list sahi dikhe
  const term = searchTerm.trim().toLowerCase();
  const filteredAccounts = accounts.filter((acc) => {
    if (!term) return true;
    return (
      acc.borrowerName?.toLowerCase().includes(term) ||
      acc.phone?.toLowerCase().includes(term) ||
      String(acc.accountNumber || "").toLowerCase().includes(term)
    );
  });

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("ALL");
    setDateRange(null);
    setView("ALL");
  };

  const openAccount = (acc) => {
    if (!canView) {
      message.warning("You do not have permission to view loan account details");
      return;
    }
    navigate(`/fintech/loan-accounts/${acc.id}`);
  };

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <Wallet className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Loan Accounts</h1>
            <p className="text-xs md:text-sm text-gray-500">
              Disbursed loan book — track EMI, outstanding and overdue demand
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchAccounts}
            disabled={loading}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm"
            title="Refresh List"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* ── Quick View Switcher ── */}
      <div className="bg-white p-3 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const active = view === v.key;
            return (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-xl border transition-all ${
                  active
                    ? "text-white border-transparent shadow-md"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                }`}
                style={active ? { backgroundColor: primaryColor } : undefined}
              >
                <Icon className="w-4 h-4" />
                <span>{v.label}</span>
              </button>
            );
          })}
          {isQuickView && (
            <span className="text-[11px] text-gray-400 font-semibold ml-1">
              Showing the dedicated {view === "NPA" ? "NPA" : "overdue"} list — status & date filters do not apply here
            </span>
          )}
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="bg-white p-4 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {/* Search Box */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Borrower, Phone, Account No..."
              className="w-full pl-10 pr-4 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
            />
          </div>

          {/* Status Dropdown */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={isQuickView}
              className="w-full px-3 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e] font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="ALL">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="CLOSED">Closed</option>
              <option value="OVERDUE">Overdue</option>
              <option value="NPA">NPA</option>
            </select>
          </div>

          {/* Date Range Picker */}
          <div>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              disabled={isQuickView}
              format="DD/MM/YYYY"
              placeholder={["Start Date", "End Date"]}
              className="w-full rounded-xl border-gray-200 py-1.5 shadow-none"
            />
          </div>

          {/* Quick Clear Button */}
          <div className="flex items-center">
            <button
              onClick={resetFilters}
              className="w-full px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* ── Loan Accounts Table ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: primaryColor, borderTopColor: "transparent" }}
            />
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center text-center gap-3 py-16 px-6">
            <AlertTriangle className="w-10 h-10 text-amber-500" />
            <h2 className="text-base font-bold text-gray-900">Could not load loan accounts</h2>
            <p className="text-sm text-gray-500 max-w-md">{loadError}</p>
            <button
              onClick={fetchAccounts}
              className="mt-1 px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                  <th className="py-4 px-4">Account No.</th>
                  <th className="py-4 px-4">Borrower</th>
                  <th className="py-4 px-4">Phone</th>
                  <th className="py-4 px-4 text-right">Principal</th>
                  <th className="py-4 px-4 text-right">EMI</th>
                  <th className="py-4 px-4 text-right">Outstanding</th>
                  <th className="py-4 px-4 text-right">Overdue</th>
                  <th className="py-4 px-4 text-center">Status</th>
                  <th className="py-4 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAccounts.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-gray-400">
                      <Wallet className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-semibold">No loan accounts found</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Try changing filters, or refresh to fetch again
                      </p>
                      <button
                        onClick={fetchAccounts}
                        className="mt-3 px-3 py-1.5 rounded-xl text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 inline-flex items-center gap-1.5"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Retry
                      </button>
                    </td>
                  </tr>
                ) : (
                  filteredAccounts.map((acc) => {
                    const statusInfo = STATUS_CONFIG[acc.status] || {
                      label: acc.status,
                      bg: "bg-gray-100",
                      text: "text-gray-700",
                      border: "border-gray-200",
                    };
                    return (
                      <tr
                        key={acc.id}
                        className="hover:bg-gray-50/60 transition-colors group cursor-pointer"
                        onClick={() => openAccount(acc)}
                      >
                        {/* Account Number */}
                        <td className="py-3.5 px-4 font-bold text-gray-900">
                          <span className="font-mono text-xs px-2 py-1 bg-gray-100 rounded-lg group-hover:bg-[#2e856e]/10 group-hover:text-[#2e856e] transition-colors">
                            {acc.accountNumber}
                          </span>
                        </td>

                        {/* Borrower */}
                        <td className="py-3.5 px-4">
                          <div className="font-semibold text-gray-900">{acc.borrowerName}</div>
                          {acc.disbursedOn || acc.disbursedAt ? (
                            <div className="text-xs text-gray-400 mt-0.5">
                              Disbursed {dayjs(acc.disbursedOn || acc.disbursedAt).isValid()
                                ? dayjs(acc.disbursedOn || acc.disbursedAt).format("DD/MM/YYYY")
                                : "-"}
                            </div>
                          ) : null}
                        </td>

                        {/* Phone */}
                        <td className="py-3.5 px-4 text-xs font-medium text-gray-600">
                          {acc.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3 text-gray-400" />
                              {acc.phone}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* Principal */}
                        <td className="py-3.5 px-4 text-right font-black text-gray-900">
                          {formatINR(acc.principalAmount)}
                        </td>

                        {/* EMI */}
                        <td className="py-3.5 px-4 text-right font-bold" style={{ color: primaryColor }}>
                          {formatINR(acc.monthlyEmi)}
                        </td>

                        {/* Outstanding */}
                        <td className="py-3.5 px-4 text-right font-bold text-blue-700">
                          {formatINR(acc.totalOutstanding)}
                        </td>

                        {/* Overdue + DPD */}
                        <td className="py-3.5 px-4 text-right">
                          <div className={`font-bold ${acc.totalOverdue > 0 ? "text-red-700" : "text-gray-500"}`}>
                            {formatINR(acc.totalOverdue)}
                          </div>
                          {acc.dpd > 0 && (
                            <div className="text-[11px] font-bold text-red-600 mt-0.5">{acc.dpd} DPD</div>
                          )}
                        </td>

                        {/* Status Badge */}
                        <td className="py-3.5 px-4 text-center">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
                          >
                            {statusInfo.label}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => openAccount(acc)}
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                            title="View Account"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !loadError && filteredAccounts.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs font-semibold text-gray-500">
            Showing {filteredAccounts.length} account{filteredAccounts.length === 1 ? "" : "s"}
          </div>
        )}
      </div>
    </div>
  );
}
